<template>
  <div class="kmap-wrap">
    <div class="kmap-head">
      <h4 class="kmap-h">From a Sardinian quarry to a Frankfurt plinth.</h4>
      <p class="kmap-sub">
        Four stages, three years, three thousand sea miles. Stone leaves Sardinia in October 1983; the finished sculpture stands at Taunusanlage on 7 September 1986.
      </p>
    </div>
    <div ref="mapHolder" class="kmap-canvas"></div>
    <ol class="kmap-legend">
      <li v-for="(s, i) in stages" :key="i">
        <span class="kmap-legend__n">{{ i + 1 }}</span>
        <span class="kmap-legend__txt">
          <strong>{{ s.caption }}</strong>
          <span class="muted"> · {{ s.date }}</span>
        </span>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { KONTINUITAT_ROUTE, KONTINUITAT_PATH } from '../data/stroobant';

const mapHolder = ref<HTMLElement | null>(null);
let map: L.Map | null = null;
let dashOffset = 0;
let dashTimer: number | null = null;

const stages = KONTINUITAT_ROUTE;

onMounted(() => {
  if (!mapHolder.value) return;
  map = L.map(mapHolder.value, {
    center: [46, 8.5],
    zoom: 4,
    minZoom: 3,
    maxZoom: 7,
    zoomControl: false,
    scrollWheelZoom: false,
    dragging: true,
    closePopupOnClick: true,
  });

  L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png', {
    subdomains: 'abcd',
    maxZoom: 19,
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  }).addTo(map);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png', {
    subdomains: 'abcd',
    maxZoom: 19,
  }).addTo(map);

  // The polyline follows the real sea-and-river waypoints (KONTINUITAT_PATH)
  // so the line bends through the Mediterranean, Gibraltar, the Atlantic,
  // the Channel, the North Sea, the Rhine and the Main — not across land.
  const pathCoords = KONTINUITAT_PATH as L.LatLngExpression[];
  const route = L.polyline(pathCoords, {
    color: '#C25A3A',
    weight: 3.5,
    dashArray: '8 6',
    opacity: 0.95,
    smoothFactor: 1.0,
  }).addTo(map!);

  // Soft halo behind the route — makes the path readable over both sea and land
  L.polyline(pathCoords, {
    color: '#F4F1EA',
    weight: 7,
    opacity: 0.55,
    smoothFactor: 1.0,
  }).addTo(map!).bringToBack();

  stages.forEach((rp, i) => {
    L.circleMarker(rp.coords, {
      radius: 9,
      color: '#7C2F19',
      weight: 2.2,
      fillColor: '#C25A3A',
      fillOpacity: 1,
    })
      .addTo(map!)
      .bindTooltip(
        `<strong>Stage ${i + 1} · ${rp.caption}</strong><br /><span style="color:#5D5D62">${rp.date ?? ''}</span>`,
        { direction: 'top', offset: [0, -10], className: 'place-tip', sticky: false },
      );

    L.marker(rp.coords, {
      icon: L.divIcon({
        className: 'kmap-num-icon',
        html: `<span class="kmap-num">${i + 1}</span>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      }),
      interactive: false,
    }).addTo(map!);
  });

  const path = route.getElement() as SVGPathElement | null;
  if (path) {
    dashTimer = window.setInterval(() => {
      dashOffset = (dashOffset + 1) % 28;
      path.style.strokeDashoffset = String(-dashOffset);
    }, 50);
  }

  // Fit bounds to the full path (waypoints included), not just the four labelled stages,
  // so the Atlantic detour is visible.
  map.fitBounds(L.latLngBounds(pathCoords), { padding: [40, 40] });
});

onBeforeUnmount(() => {
  if (dashTimer !== null) window.clearInterval(dashTimer);
  map?.remove();
});
</script>

<style scoped>
.kmap-wrap {
  background: var(--paper-deep);
  border: 1px solid var(--vein);
  border-radius: 4px;
  overflow: hidden;
  display: grid;
  /* Contain Leaflet's internal z-index range (which goes up to 700 for
     popups) so it can't bleed over the floating TrackingMap. */
  position: relative;
  z-index: 0;
  isolation: isolate;
}
.kmap-head { padding: 1.6rem 1.8rem 1rem; }
.kmap-h {
  font-family: var(--font-display);
  font-size: 1.6rem;
  font-weight: 600;
  margin: 0 0 0.4rem;
  color: var(--slate);
  line-height: 1.2;
}
.kmap-sub { font-size: 1rem; line-height: 1.55; color: var(--slate-soft); margin: 0; max-width: 56ch; }
.kmap-canvas {
  width: 100%;
  height: 460px;
  border-top: 1px solid var(--vein);
  border-bottom: 1px solid var(--vein);
}
.kmap-legend {
  list-style: none;
  margin: 0;
  padding: 1.2rem 1.8rem 1.4rem;
  display: grid;
  gap: 0.5rem;
  font-size: 0.96rem;
}
.kmap-legend li { display: flex; align-items: center; gap: 0.8rem; }
.kmap-legend__n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--accent-deep);
  color: var(--paper);
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 0.74rem;
  flex-shrink: 0;
}
.kmap-legend__txt strong { color: var(--slate); font-weight: 600; }
.kmap-legend__txt .muted { color: var(--granite); font-style: italic; }
</style>

<style>
.kmap-num-icon { background: transparent !important; border: none !important; pointer-events: none; }
.kmap-num {
  display: inline-block;
  width: 22px;
  height: 22px;
  line-height: 20px;
  border-radius: 50%;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 0.78rem;
  color: var(--paper);
  background: var(--accent-deep);
  border: 2px solid var(--paper);
  box-shadow: 0 1px 4px rgba(27,27,31,0.5);
}
</style>
